import React, { useState } from "react";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import WarehousesTutorialImage from "@me-team/common/src/images/branches/warehouse-branch.png";
import { WarehouseTutorialType } from "../../../../utils/enums";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LOCAL_STORAGE_ACTIVE, LOCAL_STORAGE_START_PATH, LOCAL_STORAGE_STEP, LOCAL_STORAGE_TYPE } from "../constants";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import TutorialSkipConfirmationContent from "../../components/TutorialSkipConfirmationContent";

interface StartWarehouseTutorialModalProps {
    show: boolean;
    handleClose: () => void;
}

const StartWarehouseTutorialModal: React.FC<StartWarehouseTutorialModalProps> = ({ show, handleClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [isSkipConfirmationOpen, setIsSkipConfirmationOpen] = useState(false);

    const handleCloseModal = () => {
        setIsSkipConfirmationOpen(false);
        handleClose();
    };

    const handleStartTutorial = (type: WarehouseTutorialType, startStep: number, firstRoute: string) => {
        localStorage.setItem(LOCAL_STORAGE_ACTIVE, "true");
        localStorage.setItem(LOCAL_STORAGE_STEP, String(startStep));
        localStorage.setItem(LOCAL_STORAGE_TYPE, type);
        localStorage.setItem(LOCAL_STORAGE_START_PATH, location.pathname);
        handleCloseModal();
        navigate(firstRoute);
    };

    return (
        <Modal show={show} onHide={handleCloseModal} centered className="px-1">
            {isSkipConfirmationOpen ? (
                <Modal.Body>
                    <TutorialSkipConfirmationContent
                        title={t("Want to learn on your own?")}
                        description={t("Save time by taking a quick training course on warehouse workflows")}
                        additionalDescription={
                            <Trans
                                i18nKey="You can take the training later by clicking the button <strong>How to work with warehouses?</strong> in the menu."
                                components={{ strong: <strong /> }}
                            />
                        }
                        onContinueTutorial={() => setIsSkipConfirmationOpen(false)}
                        onSkipTutorial={handleCloseModal}
                    />
                </Modal.Body>
            ) : (
                <>
                    <Modal.Header closeButton className="border-0 pb-6"></Modal.Header>
                    <Modal.Body className="text-center pt-0 pb-6">
                        <Row>
                            <Col className="d-flex flex-column gap-3">
                                <h5 className="mb-0">{t("Training on work with warehouses")}</h5>
                                <div>
                                    <img src={WarehousesTutorialImage} alt={t("Training on work with warehouses")} />
                                </div>
                                <p className="mb-0 px-4 fs-7">
                                    {t(
                                        "This short step-by-step guide will help you quickly learn how" +
                                            " to work with products in warehouses. You will learn how to create products, record their receipt, write-off, and transfer, as well as view reports."
                                    )}
                                </p>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4">
                        <Container className="m-0 p-0">
                            <Row className="m-0 gap-6">
                                <Button
                                    variant="primary"
                                    className="fs-7 fw-bold line-height-140"
                                    onClick={() =>
                                        handleStartTutorial(
                                            WarehouseTutorialType.WAREHOUSE_PRODUCT_MOVEMENT,
                                            0,
                                            `/warehouse/product/${currentCompanyId}/branch/${branchId}`
                                        )
                                    }
                                >
                                    {t("Movement of goods in warehouses")}
                                </Button>
                                <Button
                                    variant="orange"
                                    className="fs-7 fw-bold line-height-140"
                                    onClick={() =>
                                        handleStartTutorial(
                                            WarehouseTutorialType.CONSUMABLES_WRITE_OFF,
                                            0,
                                            `/warehouse/product/${currentCompanyId}/branch/${branchId}`
                                        )
                                    }
                                >
                                    {t("Write-off of consumables")}
                                </Button>
                                <Button
                                    variant="green"
                                    className="fs-7 fw-bold line-height-140 text-white"
                                    onClick={() =>
                                        handleStartTutorial(
                                            WarehouseTutorialType.PRODUCT_SALES,
                                            0,
                                            `/warehouse/product/${currentCompanyId}/branch/${branchId}`
                                        )
                                    }
                                >
                                    {t("Sale of products")}
                                </Button>
                                <div className="d-flex justify-content-center">
                                    <Button
                                        variant="link"
                                        className="text-grey p-0 w-auto"
                                        onClick={() => setIsSkipConfirmationOpen(true)}
                                    >
                                        {t("Skip training")}
                                    </Button>
                                </div>
                            </Row>
                        </Container>
                    </Modal.Footer>
                </>
            )}
        </Modal>
    );
};

export default StartWarehouseTutorialModal;
